import { useEffect, useState, useCallback } from 'react';
import { calendarsMgmtApi } from '../utils/api';
import {
  CalendarDays, Plus, Trash2, Pencil, Copy, ChevronRight,
  Loader2, AlertCircle, Check, X, Calendar, Zap
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { toast } from 'sonner';

const HOLIDAY_TYPES = [
  { value: 'nacional',   label: 'Nacional',   color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
  { value: 'autonomico', label: 'Autonómico', color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' },
  { value: 'local',      label: 'Local',      color: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' },
  { value: 'empresa',    label: 'Empresa',    color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
];

const typeColor = (type) =>
  (HOLIDAY_TYPES.find(t => t.value === type) || HOLIDAY_TYPES[0]).color;

const defaultHolidayForm = { date: '', name: '', type: 'empresa' };

export default function CalendarManagement() {
  const [calendars, setCalendars]       = useState([]);
  const [selectedCode, setSelectedCode] = useState(null);
  const [holidays, setHolidays]         = useState([]);
  const [loadingCals, setLoadingCals]   = useState(true);
  const [loadingHols, setLoadingHols]   = useState(false);
  const [year, setYear]                 = useState(new Date().getFullYear());

  // Dialogs
  const [dialog, setDialog]       = useState(null); // 'add-holiday' | 'edit-holiday' | 'clone' | 'delete-cal'
  const [editTarget, setEditTarget] = useState(null);
  const [holidayForm, setHolidayForm] = useState(defaultHolidayForm);
  const [cloneTarget, setCloneTarget] = useState('');
  const [saving, setSaving]       = useState(false);

  const [autoCreating, setAutoCreating] = useState(false);

  // ── Audit users' calendar_code ─────────────────────────────────────────────
  const [auditOpen, setAuditOpen] = useState(false);
  const [auditData, setAuditData] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [bulkTargetCode, setBulkTargetCode] = useState('');
  const [bulkSelected, setBulkSelected] = useState({});
  const [bulkSaving, setBulkSaving] = useState(false);

  const openAudit = async () => {
    setAuditOpen(true);
    setAuditLoading(true);
    setBulkSelected({});
    try {
      const res = await calendarsMgmtApi.auditUsers();
      setAuditData(res.data);
    } catch {
      toast.error('Error al cargar auditoría');
    } finally { setAuditLoading(false); }
  };

  const toggleUser = (uid) => {
    setBulkSelected(s => ({ ...s, [uid]: !s[uid] }));
  };
  const toggleGroup = (group) => {
    const allSelected = group.sample_users.every(u => bulkSelected[u.id]);
    setBulkSelected(s => {
      const next = { ...s };
      group.sample_users.forEach(u => { next[u.id] = !allSelected; });
      return next;
    });
  };

  const runBulkAssign = async () => {
    const ids = Object.keys(bulkSelected).filter(k => bulkSelected[k]);
    if (ids.length === 0) { toast.error('Selecciona al menos un usuario'); return; }
    if (!bulkTargetCode) { toast.error('Elige el calendario destino'); return; }
    setBulkSaving(true);
    try {
      const res = await calendarsMgmtApi.bulkAssign(ids, bulkTargetCode);
      toast.success(`${res.data.modified} usuarios actualizados a ${bulkTargetCode}`);
      setBulkSelected({}); setBulkTargetCode('');
      const fresh = await calendarsMgmtApi.auditUsers();
      setAuditData(fresh.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al asignar');
    } finally { setBulkSaving(false); }
  };

  // ── Auto-create missing calendars ──────────────────────────────────────────
  const autoCreateMissing = async () => {
    setAutoCreating(true);
    try {
      const res = await calendarsMgmtApi.autoCreateMissing();
      const { created } = res.data;
      if (created.length === 0) {
        toast.info('Todos los calendarios ya existen');
      } else {
        toast.success(`${created.length} calendarios creados: ${created.join(', ')}`);
        await loadCalendars();
        if (created.length > 0) setSelectedCode(created[0]);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al crear calendarios');
    } finally { setAutoCreating(false); }
  };
  const loadCalendars = useCallback(async () => {
    setLoadingCals(true);
    try {
      const res = await calendarsMgmtApi.list();
      setCalendars(res.data);
      if (!selectedCode && res.data.length > 0) setSelectedCode(res.data[0].calendar_code);
    } catch { toast.error('Error cargando calendarios'); }
    finally   { setLoadingCals(false); }
  }, [selectedCode]);

  // ── Load holidays for selected calendar ────────────────────────────────────
  const loadHolidays = useCallback(async () => {
    if (!selectedCode) return;
    setLoadingHols(true);
    try {
      const res = await calendarsMgmtApi.getHolidays(selectedCode, year);
      setHolidays(res.data);
    } catch { toast.error('Error cargando festivos'); }
    finally   { setLoadingHols(false); }
  }, [selectedCode, year]);

  useEffect(() => { loadCalendars(); }, []);            // eslint-disable-line
  useEffect(() => { loadHolidays(); }, [selectedCode, year]); // eslint-disable-line

  // ── Save holiday (add or edit) ──────────────────────────────────────────────
  const saveHoliday = async () => {
    if (!holidayForm.date || !holidayForm.name) {
      toast.error('Fecha y nombre son obligatorios'); return;
    }
    setSaving(true);
    try {
      if (editTarget) {
        await calendarsMgmtApi.updateHoliday(selectedCode, editTarget.id, holidayForm);
        toast.success('Festivo actualizado');
      } else {
        await calendarsMgmtApi.addHoliday(selectedCode, holidayForm);
        toast.success('Festivo añadido');
      }
      setDialog(null); setEditTarget(null); setHolidayForm(defaultHolidayForm);
      loadHolidays();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al guardar');
    } finally { setSaving(false); }
  };

  // ── Delete holiday ──────────────────────────────────────────────────────────
  const deleteHoliday = async (h) => {
    if (!window.confirm(`¿Eliminar "${h.name}" (${h.date})?`)) return;
    try {
      await calendarsMgmtApi.deleteHoliday(selectedCode, h.id);
      toast.success('Festivo eliminado');
      loadHolidays();
    } catch { toast.error('Error al eliminar'); }
  };

  // ── Clone calendar ──────────────────────────────────────────────────────────
  const cloneCalendar = async () => {
    if (!cloneTarget.trim()) { toast.error('Introduce un código para el nuevo calendario'); return; }
    setSaving(true);
    try {
      const res = await calendarsMgmtApi.clone(selectedCode, cloneTarget.trim());
      toast.success(res.data.message);
      setDialog(null); setCloneTarget('');
      await loadCalendars();
      setSelectedCode(cloneTarget.trim());
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al clonar');
    } finally { setSaving(false); }
  };

  // ── Delete calendar ─────────────────────────────────────────────────────────
  const deleteCalendar = async () => {
    setSaving(true);
    try {
      const res = await calendarsMgmtApi.deleteCalendar(selectedCode);
      toast.success(res.data.message);
      setDialog(null);
      const first = calendars.find(c => c.calendar_code !== selectedCode);
      setSelectedCode(first?.calendar_code || null);
      loadCalendars();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al eliminar');
    } finally { setSaving(false); }
  };

  const openEdit = (h) => {
    setEditTarget(h);
    setHolidayForm({ date: h.date, name: h.name, type: h.type });
    setDialog('edit-holiday');
  };

  const sortedHolidays = [...holidays].sort((a, b) => a.date.localeCompare(b.date));

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-heading flex items-center gap-2">
            <Calendar className="h-6 w-6 text-primary" />
            Gestión de Calendarios
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Cada código de tipo calendario tiene su propio conjunto de festivos.
            Condesa5 es el calendario base (Madrid HQ).
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button
            onClick={openAudit}
            variant="outline"
            data-testid="btn-audit-calendars"
            className="gap-2"
          >
            <AlertCircle className="h-4 w-4 text-orange-500" />
            Auditar usuarios
          </Button>
          <Button
            onClick={autoCreateMissing}
            disabled={autoCreating}
            variant="outline"
            data-testid="btn-auto-create-calendars"
            className="gap-2"
          >
            {autoCreating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Zap className="h-4 w-4 text-amber-500" />}
            Crear calendarios del CSV
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* ── Sidebar: calendar list ───────────────────────────────────────── */}
        <div className="lg:col-span-1 space-y-2">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide px-1 mb-1">
            Calendarios ({calendars.length})
          </p>
          {loadingCals ? (
            <div className="flex items-center gap-2 text-muted-foreground p-3">
              <Loader2 className="h-4 w-4 animate-spin" /> Cargando…
            </div>
          ) : (
            calendars.map(cal => (
              <button
                key={cal.calendar_code}
                data-testid={`cal-item-${cal.calendar_code}`}
                onClick={() => setSelectedCode(cal.calendar_code)}
                className={`w-full text-left px-3 py-2.5 rounded-lg border transition-colors flex items-center justify-between gap-2
                  ${selectedCode === cal.calendar_code
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'hover:bg-muted border-border'}`}
              >
                <span className="font-medium text-sm truncate">{cal.calendar_code}</span>
                <span className={`text-xs shrink-0 ${selectedCode === cal.calendar_code ? 'opacity-80' : 'text-muted-foreground'}`}>
                  {cal.holiday_count} fest.
                </span>
              </button>
            ))
          )}
        </div>

        {/* ── Main panel: holidays ─────────────────────────────────────────── */}
        <div className="lg:col-span-3 space-y-4">
          {selectedCode ? (
            <>
              {/* Calendar header */}
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <h2 className="text-lg font-bold font-heading">{selectedCode}</h2>
                  {selectedCode === 'Condesa5' && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary font-medium">Base</span>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  {/* Year selector */}
                  <Select value={String(year)} onValueChange={v => setYear(Number(v))}>
                    <SelectTrigger className="w-24 h-8 text-sm" data-testid="year-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[2025, 2026, 2027].map(y => (
                        <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {/* Actions */}
                  <Button
                    size="sm" variant="outline"
                    onClick={() => { setCloneTarget(''); setDialog('clone'); }}
                    data-testid="btn-clone-calendar"
                    className="gap-1.5 h-8"
                  >
                    <Copy className="h-3.5 w-3.5" />Clonar
                  </Button>
                  {selectedCode !== 'Condesa5' && (
                    <Button
                      size="sm" variant="outline"
                      onClick={() => setDialog('delete-cal')}
                      data-testid="btn-delete-calendar"
                      className="gap-1.5 h-8 text-destructive hover:bg-destructive/10 border-destructive/30"
                    >
                      <Trash2 className="h-3.5 w-3.5" />Eliminar
                    </Button>
                  )}
                  <Button
                    size="sm"
                    onClick={() => { setEditTarget(null); setHolidayForm(defaultHolidayForm); setDialog('add-holiday'); }}
                    data-testid="btn-add-holiday"
                    className="gap-1.5 h-8"
                  >
                    <Plus className="h-3.5 w-3.5" />Añadir festivo
                  </Button>
                </div>
              </div>

              {/* Holiday list */}
              {loadingHols ? (
                <div className="flex items-center gap-2 text-muted-foreground p-6">
                  <Loader2 className="h-4 w-4 animate-spin" /> Cargando festivos…
                </div>
              ) : sortedHolidays.length === 0 ? (
                <div className="border border-dashed rounded-xl p-10 text-center text-muted-foreground space-y-2">
                  <CalendarDays className="h-8 w-8 mx-auto opacity-40" />
                  <p className="text-sm">No hay festivos para {year}.</p>
                  <p className="text-xs">Puedes clonar desde otro calendario o añadir uno manualmente.</p>
                </div>
              ) : (
                <div className="border border-border rounded-xl overflow-hidden">
                  <div className="grid grid-cols-12 gap-3 px-4 py-2.5 bg-muted/50 border-b border-border text-xs font-semibold text-muted-foreground">
                    <span className="col-span-3">FECHA</span>
                    <span className="col-span-6">NOMBRE</span>
                    <span className="col-span-2">TIPO</span>
                    <span className="col-span-1 text-right">ACC.</span>
                  </div>
                  {sortedHolidays.map((h, i) => (
                    <div
                      key={h.id}
                      className={`grid grid-cols-12 gap-3 px-4 py-2.5 items-center text-sm ${i % 2 === 0 ? '' : 'bg-muted/20'}`}
                      data-testid={`holiday-row-${h.id}`}
                    >
                      <span className="col-span-3 font-mono text-xs text-muted-foreground">{h.date}</span>
                      <span className="col-span-6 font-medium truncate">{h.name}</span>
                      <span className={`col-span-2 text-xs px-1.5 py-0.5 rounded-full font-medium w-fit ${typeColor(h.type)}`}>
                        {HOLIDAY_TYPES.find(t => t.value === h.type)?.label || h.type}
                      </span>
                      <div className="col-span-1 flex justify-end gap-1">
                        <button
                          onClick={() => openEdit(h)}
                          data-testid={`edit-holiday-${h.id}`}
                          className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                        <button
                          onClick={() => deleteHoliday(h)}
                          data-testid={`delete-holiday-${h.id}`}
                          className="p-1 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : (
            <div className="flex items-center justify-center h-48 text-muted-foreground">
              Selecciona un calendario
            </div>
          )}
        </div>
      </div>

      {/* ── Dialog: Add/Edit holiday ──────────────────────────────────────────── */}
      <Dialog open={dialog === 'add-holiday' || dialog === 'edit-holiday'} onOpenChange={o => !o && setDialog(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{editTarget ? 'Editar festivo' : 'Nuevo festivo'} — {selectedCode}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <div className="space-y-1">
              <Label>Fecha</Label>
              <Input
                type="date"
                value={holidayForm.date}
                onChange={e => setHolidayForm(f => ({ ...f, date: e.target.value }))}
                data-testid="holiday-date-input"
              />
            </div>
            <div className="space-y-1">
              <Label>Nombre</Label>
              <Input
                placeholder="Ej: San Isidro"
                value={holidayForm.name}
                onChange={e => setHolidayForm(f => ({ ...f, name: e.target.value }))}
                data-testid="holiday-name-input"
              />
            </div>
            <div className="space-y-1">
              <Label>Tipo</Label>
              <Select value={holidayForm.type} onValueChange={v => setHolidayForm(f => ({ ...f, type: v }))}>
                <SelectTrigger data-testid="holiday-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {HOLIDAY_TYPES.map(t => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(null)}>Cancelar</Button>
            <Button onClick={saveHoliday} disabled={saving} data-testid="save-holiday-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
              {editTarget ? 'Guardar cambios' : 'Añadir festivo'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Dialog: Clone calendar ──────────────────────────────────────────── */}
      <Dialog open={dialog === 'clone'} onOpenChange={o => !o && setDialog(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Clonar calendario — {selectedCode}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <p className="text-sm text-muted-foreground">
              Se copiarán todos los festivos de <strong>{selectedCode}</strong> a un nuevo calendario
              que podrás modificar de forma independiente.
            </p>
            <div className="space-y-1">
              <Label>Código del nuevo calendario</Label>
              <Input
                placeholder="Ej: VSMbcn"
                value={cloneTarget}
                onChange={e => setCloneTarget(e.target.value)}
                data-testid="clone-target-input"
              />
              <p className="text-xs text-muted-foreground">
                Usa exactamente el valor del campo "Cod Tipo Calendario" del CSV.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(null)}>Cancelar</Button>
            <Button onClick={cloneCalendar} disabled={saving} data-testid="confirm-clone-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Copy className="h-4 w-4" />}
              Clonar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Dialog: Delete calendar confirm ────────────────────────────────── */}
      <Dialog open={dialog === 'delete-cal'} onOpenChange={o => !o && setDialog(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertCircle className="h-5 w-5" /> Eliminar calendario
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground py-2">
            ¿Seguro que quieres eliminar el calendario <strong>{selectedCode}</strong> y todos sus festivos?
            Esta acción no se puede deshacer.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(null)}>Cancelar</Button>
            <Button variant="destructive" onClick={deleteCalendar} disabled={saving} data-testid="confirm-delete-cal-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
              Eliminar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Audit users' calendar_code ─────────────────────────────────── */}
      <Dialog open={auditOpen} onOpenChange={setAuditOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="audit-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-orange-500" />
              Auditoría de calendarios por usuario
            </DialogTitle>
          </DialogHeader>

          {auditLoading ? (
            <div className="flex items-center justify-center py-12 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin mr-2" /> Cargando…
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Usuarios agrupados por <strong>calendar_code</strong>. Los marcados como
                <span className="mx-1 px-1.5 py-0.5 rounded bg-red-100 text-red-700 text-xs font-medium">SIN CÓDIGO</span>
                o <span className="mx-1 px-1.5 py-0.5 rounded bg-orange-100 text-orange-700 text-xs font-medium">INVÁLIDO</span>
                deben corregirse para que vean los festivos correctos.
              </p>

              {auditData.map((g, gi) => {
                const badge = g.status === 'empty'
                  ? 'bg-red-100 text-red-700 border-red-300'
                  : g.status === 'invalid'
                    ? 'bg-orange-100 text-orange-700 border-orange-300'
                    : 'bg-green-100 text-green-700 border-green-300';
                const allChecked = g.sample_users.length > 0 && g.sample_users.every(u => bulkSelected[u.id]);
                return (
                  <div key={gi} className="border rounded-lg overflow-hidden">
                    <div className="bg-muted/50 px-4 py-2.5 flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <span className={`text-xs font-mono px-2 py-1 rounded border ${badge}`}>
                          {g.calendar_code || 'SIN CÓDIGO'}
                        </span>
                        <span className="text-sm text-muted-foreground">
                          {g.user_count} usuario{g.user_count !== 1 ? 's' : ''}
                        </span>
                      </div>
                      {(g.status !== 'ok') && (
                        <button
                          onClick={() => toggleGroup(g)}
                          className="text-xs font-medium text-primary hover:underline"
                          data-testid={`audit-toggle-group-${gi}`}
                        >
                          {allChecked ? 'Deseleccionar todos' : 'Seleccionar todos'}
                        </button>
                      )}
                    </div>
                    <div className="divide-y text-sm">
                      {g.sample_users.map(u => (
                        <label
                          key={u.id}
                          className="flex items-center gap-3 px-4 py-2 hover:bg-muted/30 cursor-pointer"
                          data-testid={`audit-user-${u.id}`}
                        >
                          <input
                            type="checkbox"
                            checked={!!bulkSelected[u.id]}
                            onChange={() => toggleUser(u.id)}
                            className="h-4 w-4 accent-primary"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="truncate font-medium">{u.first_name} {u.last_name}</div>
                            <div className="text-xs text-muted-foreground truncate">
                              {u.email} {u.flex_office_desc && `· ${u.flex_office_desc}`}
                            </div>
                          </div>
                        </label>
                      ))}
                      {g.user_count > g.sample_users.length && (
                        <div className="px-4 py-2 text-xs text-muted-foreground italic">
                          + {g.user_count - g.sample_users.length} usuarios más en este grupo (mostrando {g.sample_users.length})
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}

              <div className="sticky bottom-0 bg-background pt-3 border-t flex flex-col sm:flex-row items-stretch sm:items-end gap-3">
                <div className="flex-1 space-y-1.5">
                  <Label className="text-xs">Asignar calendario a seleccionados</Label>
                  <Select value={bulkTargetCode} onValueChange={setBulkTargetCode}>
                    <SelectTrigger data-testid="audit-target-code">
                      <SelectValue placeholder="Elige calendario destino..." />
                    </SelectTrigger>
                    <SelectContent>
                      {calendars.map(c => (
                        <SelectItem key={c.calendar_code} value={c.calendar_code}>
                          {c.calendar_code} ({c.holiday_count} festivos)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  onClick={runBulkAssign}
                  disabled={bulkSaving || !bulkTargetCode || Object.values(bulkSelected).filter(Boolean).length === 0}
                  className="gap-2"
                  data-testid="audit-bulk-assign"
                >
                  {bulkSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                  Aplicar ({Object.values(bulkSelected).filter(Boolean).length})
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
